import type { SlideLayoutName } from './types';

/**
 * The design system a projected slide is built from.
 *
 * Sized for a room, not for a laptop. Everything here assumes the reader is eight metres away
 * and the projector is washing out the low end of the contrast range, which is why the type
 * starts where a document's type ends and why nothing relies on a subtle tint.
 *
 * The numbers are in `cqw` — percentages of the slide's own width — so a slide composes
 * identically on a 1440px preview and a 1920px projector instead of being tuned for one.
 */

/** Type scale, on a 1.28 ratio. `display` is for one line; `body` is the floor for a room. */
export const TYPE = {
  display: 'clamp(2.4rem, 5.6cqw, 4.6rem)',
  title: 'clamp(1.8rem, 3.6cqw, 3rem)',
  lead: 'clamp(1.35rem, 2.5cqw, 2.1rem)',
  body: 'clamp(1.15rem, 1.95cqw, 1.65rem)',
  caption: 'clamp(.9rem, 1.25cqw, 1.05rem)',
} as const;

export const SPACE = {
  pad: 'clamp(1.6rem, 3.4cqw, 3.4rem)',
  gap: 'clamp(1rem, 2.2cqw, 2.2rem)',
  tight: 'clamp(.5rem, 1cqw, 1rem)',
} as const;

/**
 * Categorical series colours, in fixed order and never cycled.
 *
 * Validated rather than chosen: the first set shipped failed three of the six checks — the
 * green read as grey, and red against amber came out at ΔE 5.3 for deuteranopia, which is
 * indistinguishable. These pass the lightness band, the chroma floor, CVD separation, the
 * normal-vision floor and contrast, in light and dark, with the brand purple kept.
 */
export const SERIES = ['#5b46d6', '#0e8f6f', '#d97706', '#b91c1c'] as const;

/**
 * The deck's own colour story, which is not the app's.
 *
 * SchoolVerse's UI is deliberately quiet — navy ink on cream, one blue accent (E-1). Projected,
 * that reads as a document. The deck keeps the brand blue so it is still recognisably SchoolVerse,
 * and adds two things the app does not have: a navy ink with some weight to it in place of
 * near-black, and a second accent so a diagram can distinguish two curves without reaching
 * for the data palette.
 *
 * Measured against a deck built by hand for the same lesson: its dominant colour was a navy
 * (#1B2A4A, used 137 times) over pale panels, with teal and rust accents. Depth of ground,
 * not decoration, is what made it look composed.
 */
export const DECK = {
  ink: '#1b2a4a',
  inkSoft: '#43526e',
  panel: '#f1f4f7',
  panelEdge: '#dde3ea',
  accent: '#2f6fb5',
  accent2: '#0e8f6f',
} as const;

/** Grid, axis and rule inks. Recessive on purpose: the data is the thing being looked at. */
export const CHART_INK = {
  axis: 'var(--c-ink-3, #6b7280)',
  grid: 'color-mix(in oklab, var(--c-ink-3, #6b7280) 22%, transparent)',
  label: 'var(--c-ink, #16171a)',
} as const;

/** Marks are thicker than a report's, because a 1px line disappears on a projector. */
export const MARK = { line: 4, axis: 2, point: 9, gridline: 1 } as const;

/**
 * Which layout a slide should use when the model did not say.
 *
 * Derived from what is actually on the slide rather than defaulted to one, because the single
 * stacked column was the whole problem: a graph with three bullets is a split, six short
 * bullets are a grid, and one sentence deserves the whole frame.
 */
export function inferLayout(elements: { kind: string; bullets?: string[] }[]): SlideLayoutName {
  const visuals = elements.filter((e) => e.kind !== 'prose');
  const prose = elements.filter((e) => e.kind === 'prose');
  const bullets = prose.flatMap((p) => p.bullets ?? []).filter(Boolean);

  if (visuals.length && prose.length) return 'split';
  if (visuals.length) return 'visual';
  if (bullets.length === 1) return 'statement';
  if (bullets.length >= 4 && bullets.every((b) => b.split(/\s+/).length <= 8)) return 'grid';
  return 'steps';
}
